import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import {
  Download, Trash2, Edit2, GripVertical, MoreHorizontal,
  Copy, FileText, Pin, PinOff,
} from 'lucide-react';
import { classifyAutomaton } from '../../utils/automataHelpers';

const TYPE_BADGE_CLASSES = {
  DFA: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20',
  NFA: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20',
  PDA: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20',
  TM: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 border border-teal-500/20',
};

export function TypeBadge({ automaton, testId }) {
  const cls = automaton.type === 'PDA' ? 'PDA' : classifyAutomaton(automaton);
  return (
    <span
      data-testid={testId}
      className={`shrink-0 px-1.5 py-0.5 rounded font-mono text-[10px] font-bold uppercase tracking-wide leading-none ${TYPE_BADGE_CLASSES[cls] || TYPE_BADGE_CLASSES.NFA}`}
    >{cls}</span>
  );
}

export function AutomatonRow({
  automaton, isActive, isPinned, dragEnabled, dropIndicatorTop, dropIndicatorBottom,
  onDragStart, onDragEnd, onDragOver, onDrop,
  onLoad, onContextMenu, onTogglePin,
  onOpenRenameDialog, onDuplicate, onExport, onSaveAsDocx, onDelete,
  rowTestIdPrefix, isReadOnly = false,
}) {
  return (
    <div
      className={`group/item relative flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm cursor-pointer transition-colors duration-150 ${
        isActive
          ? 'bg-primary/10 text-primary font-semibold border-l-2 border-primary -ml-[2px] pl-[9px]'
          : 'text-foreground/80 hover:bg-accent/60 hover:text-foreground hover:shadow-sm'
      }`}
      draggable={dragEnabled}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={onLoad}
      onContextMenu={onContextMenu}
      data-testid={`${rowTestIdPrefix}-${automaton.automaton_id}`}
    >
      {dropIndicatorTop && <div className="absolute -top-[1px] left-2 right-2 h-0.5 bg-primary rounded-full" />}
      {dropIndicatorBottom && <div className="absolute -bottom-[1px] left-2 right-2 h-0.5 bg-primary rounded-full" />}
      {dragEnabled && <GripVertical className="w-3 h-3 text-muted-foreground/30 shrink-0 cursor-grab" />}
      <span className="flex-1 min-w-0 truncate font-medium text-xs">{automaton.name}</span>
      <TypeBadge automaton={automaton} testId={`automaton-type-${automaton.automaton_id}`} />
      <button
        onClick={(e) => { e.stopPropagation(); if (!isReadOnly) onTogglePin(automaton.automaton_id); }}
        disabled={isReadOnly}
        title={isReadOnly ? 'Read-only while viewing as another user' : undefined}
        className={`shrink-0 p-0.5 rounded transition-opacity disabled:cursor-not-allowed ${
          isPinned ? 'opacity-100 text-amber-500' : 'opacity-0 group-hover/item:opacity-100 text-muted-foreground hover:text-amber-500'
        } ${isReadOnly ? 'group-hover/item:opacity-40' : ''}`}
        data-testid={`pin-automaton-btn-${automaton.automaton_id}`}
      >
        <Pin className={`w-3.5 h-3.5 ${isPinned ? 'fill-amber-400' : ''}`} />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="shrink-0 p-0.5 hover:bg-accent rounded opacity-0 group-hover/item:opacity-100 transition-opacity"
            onClick={(e) => e.stopPropagation()}
            data-testid={`automaton-menu-${automaton.automaton_id}`}
          >
            <MoreHorizontal className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem disabled={isReadOnly} onClick={() => onTogglePin(automaton.automaton_id)} data-testid={`automaton-menu-pin-${automaton.automaton_id}`}>
            {isPinned ? <PinOff className="w-4 h-4 mr-2" /> : <Pin className="w-4 h-4 mr-2" />} {isPinned ? 'Unpin' : 'Pin to top'}
          </DropdownMenuItem>
          <DropdownMenuItem disabled={isReadOnly} onClick={() => onOpenRenameDialog(automaton.automaton_id)} data-testid={`automaton-menu-rename-${automaton.automaton_id}`}>
            <Edit2 className="w-4 h-4 mr-2" /> Rename
          </DropdownMenuItem>
          <DropdownMenuItem disabled={isReadOnly} onClick={() => onDuplicate(automaton.automaton_id)} data-testid={`automaton-menu-duplicate-${automaton.automaton_id}`}>
            <Copy className="w-4 h-4 mr-2" /> Duplicate
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onExport(automaton.automaton_id)} data-testid={`automaton-menu-export-${automaton.automaton_id}`}>
            <Download className="w-4 h-4 mr-2" /> Export
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onSaveAsDocx(automaton.automaton_id)} data-testid={`automaton-menu-save-docx-${automaton.automaton_id}`}>
            <FileText className="w-4 h-4 mr-2" /> Save as Word Document
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={isReadOnly}
            className="text-destructive focus:text-destructive"
            onClick={() => onDelete(automaton.automaton_id)}
            data-testid={`automaton-menu-delete-${automaton.automaton_id}`}
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
